import React, {
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    memo,
    useCallback,
} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { applyAbilities } from '../utilities';
import { computeTrophyDelta } from '../utilities/index';
import { PlayingCard } from './PlayingCard';
import { UserContext } from '../contexts/UserContext';
import CombatDialogBackground from '../data/CombatDialogBackground.png';
import Greenfields from '../data/Greenfields.png';

// 🔥 Firebase Storage for preloading art (fallback only)
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

/** =========================================================
 * THEME TOKENS
 * ========================================================= */
const GOLD = '#d4af37';
const TEXT_WARM = '#f9f6e3';
const DEBUFF_RED = '#d24d57'; // elegant warm red for debuffs

/** =========================================================
 * RARITY COLORS (your mapping, with GOLD included)
 * ========================================================= */
export const rarityColor = (rarityRaw) => {
    const r = String(rarityRaw || '').toLowerCase();
    if (r === 'common') return '#8fa58f';
    if (r === 'uncommon') return '#3f7a46';
    if (r === 'rare') return '#7aa3d6';
    if (r === 'high rare') return '#1d4f91';
    if (r === 'super rare') return '#b68ec9';
    if (r === 'super super rare') return '#5a2b86';
    if (r === 'legendary') return GOLD;
    if (r === 'mythic') return '#e6d685';
    return '#a8a8a8';
};

/** =========================================================
 * ANIMATION TIMING (slowed ~50%)
 * ========================================================= */
const ACTIVE_POP_MS = 1500;
const SETTLE_MS = 450;
const SPRING_STIFFNESS = 180;
const SPRING_DAMPING = 28;
const START_BANNER_MS = 1700; // 1.5–2 seconds intro "Start" dialog

/** =========================================================
 * UTIL
 * ========================================================= */
const fmt = (n) => Number(n || 0).toLocaleString();

// Does this card actually have an ability?
const hasAbility = (card) => Array.isArray(card?.ability) && card.ability.length > 0;

/**
 * Humanize keys for UI display:
 * - soulDust -> Soul Dust
 * - basic -> Basic
 * - rarePlus -> Rare Plus
 * - highRarePlus -> High Rare Plus
 * - fallback: camelCase / snake_case -> Title Case
 */
const humanizeKey = (raw) => {
    const key = String(raw || '');
    if (!key) return '';

    if (key === 'soulDust') return 'Soul Dust';
    if (key === 'basic') return 'Basic';
    if (key === 'rarePlus') return 'Rare Plus';
    if (key === 'highRarePlus') return 'High Rare Plus';

    const withSpaces = key.replace(/([A-Z])/g, ' $1');
    return withSpaces
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
        .trim();
};

const normalizeReward = (r) => ({
    coins: Number(r?.coins ?? 0),
    drawTickets: Object.fromEntries(
        Object.entries(r?.drawTickets || {}).map(([k, v]) => [k, Number(v ?? 0)])
    ),
    items: Object.fromEntries(
        Object.entries(r?.items || {}).map(([k, v]) => [k, Number(v ?? 0)])
    ),
    cards: Array.isArray(r?.cards) ? r.cards : [],
});

const effectGlowForDelta = (before = {}, after = {}) => {
    const bAtk = Number(before.attack || 0);
    const bDef = Number(before.defense || 0);
    const aAtk = Number(after.attack || 0);
    const aDef = Number(after.defense || 0);
    const delta = aAtk + aDef - (bAtk + bDef);
    if (delta > 0) return GOLD;
    if (delta < 0) return DEBUFF_RED;
    return alpha(GOLD, 0.7);
};

const buildGlowMap = (beforeArr = [], afterArr = [], indices = []) => {
    const map = {};
    const n = Math.min(beforeArr.length, afterArr.length);
    indices.forEach((i) => {
        if (i >= 0 && i < n) {
            map[i] = effectGlowForDelta(beforeArr[i], afterArr[i]);
        }
    });
    return map;
};

const buildBuffStatusMap = (beforeArr = [], afterArr = [], indices = []) => {
    const map = {};
    const n = Math.min(beforeArr.length, afterArr.length);
    indices.forEach((i) => {
        if (i < 0 || i >= n) return;
        const bAtk = Number(beforeArr[i]?.attack || 0);
        const bDef = Number(beforeArr[i]?.defense || 0);
        const aAtk = Number(afterArr[i]?.attack || 0);
        const aDef = Number(afterArr[i]?.defense || 0);
        const delta = aAtk + aDef - (bAtk + bDef);
        if (delta > 0) map[i] = 'Buffed';
        else if (delta < 0) map[i] = 'Debuffed';
    });
    return map;
};

/** =========================================================
 * ORDERING HELPERS
 * - Player: use activeOrder
 * - Enemy:  use defenseOrder
 * ========================================================= */
const orderCardsByField = (cards, field) => {
    if (!Array.isArray(cards) || cards.length === 0) return [];
    const anyHasOrder = cards.some((c) => typeof c?.[field] === 'number');
    if (!anyHasOrder) return cards;

    return [...cards].sort((a, b) => {
        const ao = typeof a?.[field] === 'number' ? a[field] : Number.MAX_SAFE_INTEGER;
        const bo = typeof b?.[field] === 'number' ? b[field] : Number.MAX_SAFE_INTEGER;
        if (ao === bo) return 0;
        return ao - bo;
    });
};

/** =========================================================
 * Resolve ability to use for the current evolution stage
 * ========================================================= */
function resolveAbilityForStage(card = {}) {
    const stageStr = String(card?.evolutionStage ?? '0'); // '0','1','2','3'
    const evoAbility =
        stageStr !== '0' &&
        card?.evolutions &&
        Array.isArray(card.evolutions?.[stageStr]?.ability) &&
        card.evolutions[stageStr].ability.length
            ? card.evolutions[stageStr].ability
            : null;

    const baseAbility = Array.isArray(card.ability) ? card.ability : [];
    const chosen = evoAbility ?? baseAbility;

    return { ...card, ability: chosen };
}

/** =========================================================
 * Passive Boosts (Guild + Faction)
 * ========================================================= */
function makePassiveBoosts(cards = [], userData) {
    const isInGuild = !!(userData?.guildId || userData?.guild || userData?.inGuild);
    const playerFaction = String(userData?.faction || '').toLowerCase(); // "man" | "beast" (expected)
    const boosted = [];
    const guildIdx = [];
    const factionIdx = [];
    const labelMap = {}; // index -> ["Guild Boost", "Faction Boost"]

    for (let i = 0; i < Math.min(5, cards.length); i++) {
        const c = cards[i] || {};
        let mult = 1;

        if (isInGuild) {
            mult *= 1.05;
            guildIdx.push(i);
            labelMap[i] = [...(labelMap[i] || []), 'Guild Boost'];
        }

        const cardClass = String(c?.classification || c?.type || '').toLowerCase();
        if (playerFaction && cardClass && playerFaction === cardClass) {
            mult *= 1.05;
            factionIdx.push(i);
            labelMap[i] = [...(labelMap[i] || []), 'Faction Boost'];
        }

        if (mult !== 1) {
            boosted.push({
                ...c,
                attack: Math.round(Number(c.attack || 0) * mult),
                defense: Math.round(Number(c.defense || 0) * mult),
            });
        } else {
            boosted.push(c);
        }
    }

    return {
        boosted,
        guildIdx,
        factionIdx,
        labelMap,
        any: guildIdx.length > 0 || factionIdx.length > 0,
    };
}

/** =========================================================
 * IMAGE PRELOAD HELPERS (FALLBACK)
 * - only used if parent does NOT pass getImageForCard
 * ========================================================= */
const imageUrlCacheCombat = new Map();

const evoClamp = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return 1;
    return Math.max(1, Math.min(3, x));
};

const buildCanonicalPath = (cardId, evolutionStage) => {
    const evo = evoClamp(evolutionStage);
    return `cards/card-images/${cardId}_evo${evo}.png`;
};

const candidatePathsForCombat = (card, baseCard) => {
    const evo = evoClamp(card?.evolutionStage || baseCard?.evolutionStage || 1);
    const cardId = card?.cardId || baseCard?.cardId || baseCard?.id;

    const paths = [];
    if (baseCard && baseCard.images && baseCard.images[`evo${evo}`]) {
        paths.push(baseCard.images[`evo${evo}`]);
    }
    if (baseCard && typeof baseCard.image === 'string' && baseCard.image) {
        paths.push(baseCard.image);
    }
    if (cardId) paths.push(buildCanonicalPath(cardId, evo));

    const dedup = [...new Set(paths)];
    if (cardId) dedup.push(buildCanonicalPath(cardId, 1)); // fallback evo1
    return { list: dedup, cardId };
};

const getUrlForStoragePathCombat = async (path) => {
    if (imageUrlCacheCombat.has(path)) return imageUrlCacheCombat.get(path);
    const storage = getStorage();
    const url = await getDownloadURL(ref(storage, path));
    imageUrlCacheCombat.set(path, url);
    return url;
};

const resolveCardImageUrlForCombat = async (card, baseCard) => {
    const { list, cardId } = candidatePathsForCombat(card, baseCard);
    if (!cardId) throw new Error('Missing cardId for image resolution (combat).');

    let lastErr = null;
    for (const p of list) {
        try {
            const url = await getUrlForStoragePathCombat(p);
            return url;
        } catch (e) {
            lastErr = e;
        }
    }
    throw lastErr || new Error('Unable to resolve image URL (combat).');
};

const preloadImageTag = (src) =>
    new Promise((resolve) => {
        if (!src || typeof Image === 'undefined') return resolve();
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
    });

const getCardImageKey = (card) => {
    if (!card?.cardId) return null;
    const evo = evoClamp(card?.evolutionStage || 1);
    return `${card.cardId}-evo${evo}`;
};

const preloadArtForCombatCards = async (cards, globalCards, setImageMap) => {
    if (!Array.isArray(cards) || !cards.length || !globalCards) return;

    const entries = await Promise.all(
        cards.map(async (card) => {
            const key = getCardImageKey(card);
            if (!key) return null;

            try {
                const base = globalCards[card.cardId] || card || null;
                if (!base) return null;
                const url = await resolveCardImageUrlForCombat(card, base);
                await preloadImageTag(url);
                return [key, url];
            } catch (e) {
                console.warn('[CombatDialog] Failed to preload art for card', card?.cardId, e);
                return null;
            }
        })
    );

    const updates = {};
    for (const pair of entries) {
        if (!pair) continue;
        const [key, url] = pair;
        updates[key] = url;
    }

    if (Object.keys(updates).length) {
        setImageMap((prev) => ({ ...prev, ...updates }));
    }
};

/** =========================================================
 * Accurate per-source affected indices via simulation (no guessing)
 * - Does NOT change applyAbilities; only mirrors logic for visualization
 * ========================================================= */
const deepClone = (x) => JSON.parse(JSON.stringify(x));

const numSafe = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const normClass = (s) => String(s || '').trim().toLowerCase();

function applyAbilitiesSingleSource(abilitySourceCards, baselineSelfCards, baselineOppCards, sourceIdx) {
    const modifiedSelf = deepClone(baselineSelfCards || []);
    const modifiedTargets = deepClone(baselineOppCards || []);

    // ensure numeric stats
    for (const c of modifiedSelf) {
        c.attack = numSafe(c.attack);
        c.defense = numSafe(c.defense);
    }
    for (const c of modifiedTargets) {
        c.attack = numSafe(c.attack);
        c.defense = numSafe(c.defense);
    }

    const originalSource = abilitySourceCards?.[sourceIdx];
    if (!originalSource?.triggered || !Array.isArray(originalSource?.ability)) {
        return { modifiedSelf, modifiedTargets };
    }

    for (const effect of originalSource.ability) {
        if (!effect?.target || (!effect.increase && !effect.decrease)) continue;

        const parts = String(effect.target).split('.');
        if (parts.length !== 2) continue;

        const targetClassNorm = normClass(parts[0]);
        const targetStat =
            parts[1] === 'attack' ? 'attack' : parts[1] === 'defense' ? 'defense' : null;
        if (!targetStat) continue;

        const applyTo = effect.targetType === 'self' ? modifiedSelf : modifiedTargets;

        for (let i = 0; i < applyTo.length; i++) {
            const target = applyTo[i];
            if (normClass(target?.classification) !== targetClassNorm) continue;

            const baseVal = numSafe(target[targetStat]);
            const flat = numSafe(effect.flatAmount);
            const pct = numSafe(effect.percent);

            const percentPart = pct ? baseVal * (pct / 100) : 0;
            const delta = Math.floor(flat + percentPart);
            if (!delta) continue;

            if (effect.increase) {
                target[targetStat] = baseVal + delta;
            } else if (effect.decrease) {
                target[targetStat] = Math.max(0, baseVal - delta);
            }
        }
    }

    return { modifiedSelf, modifiedTargets };
}

function buildPerSourceDiffMaps(abilitySourceCards, baselineSelfCards, baselineOppCards) {
    const oppMap = {};
    const selfMap = {};
    const maxSlots = 5;

    const sources = Array.isArray(abilitySourceCards) ? abilitySourceCards.slice(0, maxSlots) : [];
    const baseSelf = Array.isArray(baselineSelfCards) ? baselineSelfCards.slice(0, maxSlots) : [];
    const baseOpp = Array.isArray(baselineOppCards) ? baselineOppCards.slice(0, maxSlots) : [];

    const changedIndices = (before, after) => {
        const out = [];
        const n = Math.min(before.length, after.length, maxSlots);
        for (let i = 0; i < n; i++) {
            const b = before[i] || {};
            const a = after[i] || {};
            if ((b.attack || 0) !== (a.attack || 0) || (b.defense || 0) !== (a.defense || 0)) {
                out.push(i);
            }
        }
        return out;
    };

    for (let sourceIdx = 0; sourceIdx < sources.length; sourceIdx++) {
        const src = sources[sourceIdx];
        if (!src?.triggered || !Array.isArray(src?.ability) || src.ability.length === 0) continue;

        const { modifiedSelf, modifiedTargets } = applyAbilitiesSingleSource(
            sources,
            baseSelf,
            baseOpp,
            sourceIdx
        );

        selfMap[sourceIdx] = changedIndices(baseSelf, modifiedSelf);
        oppMap[sourceIdx] = changedIndices(baseOpp, modifiedTargets);
    }

    return { oppMap, selfMap };
}

/** =========================================================
 * Memoized PlayingCard for all usages in this file
 * ========================================================= */
const MemoPlayingCard = memo(PlayingCard);

/** =========================================================
 * FloatingBanner (stable, top-level)
 * ========================================================= */
function FloatingBanner({ children, offset = -15 }) {
    return (
        <Box
            sx={{
                position: 'absolute',
                top: offset,
                left: '50%',
                transform: 'translateX(-50%)',
                px: 1,
                py: 0.25,
                borderRadius: 1.25,
                border: `1px solid ${alpha(GOLD, 0.75)}`,
                background: `linear-gradient(180deg, ${alpha('#000', 0.75)} 0%, ${alpha(
                    '#0b0b0b',
                    0.9
                )} 100%)`,
                boxShadow: `0 0 0 1px ${alpha(GOLD, 0.2)} inset, 0 8px 20px ${alpha(
                    GOLD,
                    0.18
                )}`,
                pointerEvents: 'none',
                zIndex: 2,
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    color: TEXT_WARM,
                    fontFamily: `'Cinzel', serif`,
                    letterSpacing: '.6px',
                    textShadow: `0 0 10px ${alpha(GOLD, 0.25)}`,
                    whiteSpace: 'nowrap',
                }}
            >
                {children}
            </Typography>
        </Box>
    );
}

/** =========================================================
 * AnimatedCard (stable, top-level)
 * ========================================================= */
function AnimatedCard({
    card,
    i,
    side,
    isAffected,
    affectGlowMap,
    boostLabels,
    buffStatus,
    activeSide,
    activeIndex,
    showBoostBadges,
    getImageForCard,
}) {
    const hasCardAbility = hasAbility(card);
    const isActive = hasCardAbility && activeSide === side && activeIndex === i && !!card?.triggered;

    const srcGlow = rarityColor(card?.rarity);
    const baseShadow = `0 0 0 1px ${alpha(GOLD, 0.18)} inset`;

    const perTargetGlow = affectGlowMap?.[i];

    let boxShadow = baseShadow;
    if (isActive) {
        boxShadow = `0 0 0 2px ${alpha(srcGlow, 0.85)}, 0 0 28px 8px ${alpha(srcGlow, 0.38)}`;
    } else if (isAffected) {
        const g = perTargetGlow || GOLD;
        boxShadow = `0 0 0 2px ${alpha(g, 0.9)}, 0 0 24px 6px ${alpha(g, 0.45)}`;
    }

    const targetScale = isActive ? 1.25 : isAffected ? 1.1 : 1;

    return (
        <motion.div
            initial={false}
            animate={{ scale: targetScale }}
            transition={{ type: 'spring', stiffness: SPRING_STIFFNESS, damping: SPRING_DAMPING }}
            style={{ borderRadius: 12, padding: 6, boxShadow, position: 'relative' }}
        >
            {isActive && <FloatingBanner offset={-32}>Ability Triggered</FloatingBanner>}

            {Array.isArray(boostLabels) && boostLabels.length > 0 && showBoostBadges && (
                <FloatingBanner>{boostLabels.join(' • ')}</FloatingBanner>
            )}

            {/* ✅ FIX: Never show Buffed/Debuffed overlay when boost badges are showing */}
            {isAffected && buffStatus && !showBoostBadges && <FloatingBanner>{buffStatus}</FloatingBanner>}

            <MemoPlayingCard
                card={card}
                size="sm"
                showStats={false}
                showName
                showRarityBadge
                showClassificationBadge
                disableFitText
                imageSrc={getImageForCard?.(card)} // ✅ uses parent-provided preloaded art
            />
        </motion.div>
    );
}

/** =========================================================
 * DeckRow (stable, top-level)
 * ========================================================= */
function DeckRow({
    cards,
    side,
    title,
    affectedIndices = [],
    affectGlowMap = {},
    boostBadgesMap = {},
    buffStatusMap = {},
    activeSide,
    activeIndex,
    showBoostBadges,
    getImageForCard,
}) {
    const row = (cards || []).slice(0, 5);
    return (
        <Box sx={{ mb: 2 }}>
            <Typography
                sx={{
                    color: alpha(TEXT_WARM, 0.9),
                    mb: 1,
                    fontFamily: `'Cinzel', serif`,
                    letterSpacing: '.6px',
                }}
            >
                {title}
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                    gap: 2,
                    alignItems: 'stretch',
                    justifyItems: 'center',
                }}
            >
                {row.map((card, i) => {
                    const key =
                        card?.ownedCardId ||
                        card?.instanceId ||
                        card?.userCardId ||
                        card?.cardId ||
                        card?.id ||
                        `slot-${side}-${i}`;

                    return (
                        <AnimatedCard
                            key={key}
                            card={card}
                            i={i}
                            side={side}
                            isAffected={affectedIndices.includes(i)}
                            affectGlowMap={affectGlowMap}
                            boostLabels={boostBadgesMap[i]}
                            buffStatus={buffStatusMap[i]}
                            activeSide={activeSide}
                            activeIndex={activeIndex}
                            showBoostBadges={showBoostBadges}
                            getImageForCard={getImageForCard}
                        />
                    );
                })}
            </Box>
        </Box>
    );
}

/** =========================================================
 * MAIN COMPONENT
 * ========================================================= */
function CombatDialog({
    open,
    onClose,
    enemy,
    playerCards,
    playerTrophies = 0,
    onResolved,
    playerId,
    mode = 'arena',
    campaignReward = null,

    // 🔥 NEW: parent can pass preloaded image resolver
    getImageForCard: getImageForCardProp,
}) {
    const PaperBox = {
        backgroundImage: `url(${mode === 'campaign' ? Greenfields : CombatDialogBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#000',
        color: TEXT_WARM,
        border: `1.5px solid ${alpha(GOLD, 0.5)}`,
        boxShadow: `
          0 0 0 1px ${alpha(GOLD, 0.12)} inset,
          0 20px 50px ${alpha(GOLD, 0.12)},
          0 6px 16px rgba(0,0,0,0.65)
        `,
        borderRadius: 2,
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    };

    const TitleText = {
        color: TEXT_WARM,
        fontFamily: `'Cinzel', serif`,
        letterSpacing: '1px',
        textShadow: `0 0 16px ${alpha(GOLD, 0.25)}`,
    };

    const { userData, globalCards } = useContext(UserContext) || {};
    const resolvedPlayerId = playerId || userData?.uid || null;

    const safeEnemy = enemy || {};
    const unorderedEnemyCards = Array.isArray(safeEnemy.cards) ? safeEnemy.cards : [];
    const unorderedPlayerCards = Array.isArray(playerCards) ? playerCards : [];

    // MEMOIZED ORDERING
    const basePlayerCards = useMemo(
        () => orderCardsByField(unorderedPlayerCards, 'activeOrder'),
        [unorderedPlayerCards]
    );
    const baseEnemyCards = useMemo(
        () => orderCardsByField(unorderedEnemyCards, 'defenseOrder'),
        [unorderedEnemyCards]
    );

    /** =========================================================
     * Passive boosts BEFORE abilities
     * ========================================================= */
    const {
        boosted: preBoostPlayerCards,
        guildIdx,
        factionIdx,
        labelMap: passiveBoostLabelMap,
        any: anyPassiveBoosts,
    } = useMemo(() => makePassiveBoosts(basePlayerCards, userData), [basePlayerCards, userData]);

    /** =========================================================
     * Resolve evo-correct abilities BEFORE triggering
     * ========================================================= */
    const readyPlayerCards = useMemo(() => preBoostPlayerCards.map(resolveAbilityForStage), [
        preBoostPlayerCards,
    ]);
    const readyEnemyCards = useMemo(() => baseEnemyCards.map(resolveAbilityForStage), [baseEnemyCards]);

    const primeStats = (cards) =>
        cards.map((c) => ({
            ...c,
            attack: Number.isFinite(Number(c.attack)) ? Number(c.attack) : 0,
            defense: Number.isFinite(Number(c.defense)) ? Number(c.defense) : 0,
        }));

    const primedPlayerCards = useMemo(() => primeStats(readyPlayerCards), [readyPlayerCards]);
    const primedEnemyCards = useMemo(() => primeStats(readyEnemyCards), [readyEnemyCards]);

    // ===== Trigger decisions (per open)
    const triggerChances = [100, 75, 50, 25, 25];

    const getTriggeredCards = (cards) =>
        cards.map((card, index) => {
            const canTrigger = hasAbility(card);
            const shouldTrigger = canTrigger && Math.random() * 100 < (triggerChances[index] || 0);
            return { ...card, triggered: shouldTrigger };
        });

    /**
     * battleKey increments exactly once each time dialog opens.
     * This prevents trigger staleness/drift while still locking triggers per open cycle.
     */
    const [battleKey, setBattleKey] = useState(0);
    useEffect(() => {
        if (open) setBattleKey((k) => k + 1);
    }, [open]);

    const triggeredPlayerCards = useMemo(
        () => getTriggeredCards(primedPlayerCards),
        [battleKey, primedPlayerCards]
    );
    const triggeredEnemyCards = useMemo(
        () => getTriggeredCards(primedEnemyCards),
        [battleKey, primedEnemyCards]
    );

    // ===== Mechanics: apply abilities (player first, then enemy)
    const { modifiedSelf: updatedPlayerCards, modifiedTargets: updatedEnemyCards } = useMemo(
        () => applyAbilities(triggeredPlayerCards, primedEnemyCards),
        [triggeredPlayerCards, primedEnemyCards]
    );

    const { modifiedSelf: finalEnemyCards, modifiedTargets: finalPlayerCards } = useMemo(
        () => applyAbilities(triggeredEnemyCards, updatedPlayerCards),
        [triggeredEnemyCards, updatedPlayerCards]
    );

    // COMPUTE TOTALS — but reveal only in banner
    const playerAttack = useMemo(() => finalPlayerCards.reduce((s, c) => s + (c.attack || 0), 0), [
        finalPlayerCards,
    ]);
    const enemyDefense = useMemo(() => finalEnemyCards.reduce((s, c) => s + (c.defense || 0), 0), [
        finalEnemyCards,
    ]);

    const playerPower = useMemo(() => basePlayerCards.reduce((s, c) => s + (c?.defense || 0), 0), [
        basePlayerCards,
    ]);
    const enemyPower = useMemo(
        () =>
            typeof safeEnemy.powerLevel === 'number'
                ? safeEnemy.powerLevel
                : baseEnemyCards.reduce((s, c) => s + (c?.defense || 0), 0),
        [safeEnemy.powerLevel, baseEnemyCards]
    );

    const playerWins = playerAttack > enemyDefense;
    const resultText = playerWins ? 'VICTORY' : 'DEFEAT';

    // Campaign reward
    const normalizedCampaignReward = useMemo(() => normalizeReward(campaignReward), [campaignReward]);
    const rewardLines = useMemo(() => {
        if (!normalizedCampaignReward) return [];
        const lines = [];
        const { coins, drawTickets, items, cards } = normalizedCampaignReward;

        if (coins > 0) lines.push(`+${fmt(coins)} coin${coins === 1 ? '' : 's'}`);

        Object.entries(drawTickets || {}).forEach(([k, v]) => {
            if (v > 0) {
                const label = humanizeKey(k);
                lines.push(`+${v} ${label} Ticket${v > 1 ? 's' : ''}`);
            }
        });

        Object.entries(items || {}).forEach(([k, v]) => {
            if (v > 0) {
                const label = humanizeKey(k);
                lines.push(`+${v} ${label}`);
            }
        });

        if (Array.isArray(cards) && cards.length > 0) lines.push(`Card reward x${cards.length}`);

        return lines;
    }, [normalizedCampaignReward]);
    const hasCampaignReward = rewardLines.length > 0;

    // ===== Trophies (arena only)
    const trophiesEnabled = mode === 'arena';
    const safePT = Math.max(0, playerTrophies | 0);
    const safeET = Math.max(0, Number(safeEnemy.trophies || 0));
    const safePP = Math.max(1, playerPower | 0);
    const safeEP = Math.max(1, enemyPower | 0);

    const trophyDelta = computeTrophyDelta({
        winnerTrophies: playerWins ? safePT : safeET,
        loserTrophies: playerWins ? safeET : safePT,
        winnerPower: playerWins ? safePP : safeEP,
        loserPower: playerWins ? safeEP : safePP,
    });

    /** =========================================================
     * EFFECT VISUALIZATION (accurate + complete)
     * - Track BOTH:
     *   1) self/team changes from own abilities
     *   2) incoming changes from opponent abilities
     * ========================================================= */
    const changedIndices = (before, after, fields = ['attack', 'defense']) => {
        const out = [];
        const n = Math.min(before.length, after.length, 5);
        for (let i = 0; i < n; i++) {
            const b = before[i] || {};
            const a = after[i] || {};
            if (fields.some((f) => (b?.[f] || 0) !== (a?.[f] || 0))) out.push(i);
        }
        return out;
    };

    // PLAYER -> ENEMY (player ability effects onto enemy)
    const enemyAffectedByPlayerIdx = useMemo(
        () => changedIndices(primedEnemyCards, updatedEnemyCards),
        [primedEnemyCards, updatedEnemyCards]
    );

    // PLAYER -> SELF (player self/team buffs from player abilities)
    const playerSelfAffectedByPlayerIdx = useMemo(
        () => changedIndices(primedPlayerCards, updatedPlayerCards),
        [primedPlayerCards, updatedPlayerCards]
    );

    // ENEMY -> PLAYER (enemy ability effects onto player)
    const playerAffectedByEnemyIdx = useMemo(
        () => changedIndices(updatedPlayerCards, finalPlayerCards),
        [updatedPlayerCards, finalPlayerCards]
    );

    // ENEMY -> SELF (enemy self/team buffs from enemy abilities)
    const enemySelfAffectedByEnemyIdx = useMemo(
        () => changedIndices(updatedEnemyCards, finalEnemyCards),
        [updatedEnemyCards, finalEnemyCards]
    );

    // Glow maps
    const enemyGlowMap_PlayerToEnemy = useMemo(
        () => buildGlowMap(primedEnemyCards, updatedEnemyCards, enemyAffectedByPlayerIdx),
        [primedEnemyCards, updatedEnemyCards, enemyAffectedByPlayerIdx]
    );

    const playerGlowMap_EnemyToPlayer = useMemo(
        () => buildGlowMap(updatedPlayerCards, finalPlayerCards, playerAffectedByEnemyIdx),
        [updatedPlayerCards, finalPlayerCards, playerAffectedByEnemyIdx]
    );

    const playerSelfGlowMap_PlayerSelf = useMemo(
        () => buildGlowMap(primedPlayerCards, updatedPlayerCards, playerSelfAffectedByPlayerIdx),
        [primedPlayerCards, updatedPlayerCards, playerSelfAffectedByPlayerIdx]
    );

    const enemySelfGlowMap_EnemySelf = useMemo(
        () => buildGlowMap(updatedEnemyCards, finalEnemyCards, enemySelfAffectedByEnemyIdx),
        [updatedEnemyCards, finalEnemyCards, enemySelfAffectedByEnemyIdx]
    );

    // Buff/Debuff label maps
    const enemyBuffStatusMap_PlayerToEnemy = useMemo(
        () => buildBuffStatusMap(primedEnemyCards, updatedEnemyCards, enemyAffectedByPlayerIdx),
        [primedEnemyCards, updatedEnemyCards, enemyAffectedByPlayerIdx]
    );

    const playerBuffStatusMap_EnemyToPlayer = useMemo(
        () => buildBuffStatusMap(updatedPlayerCards, finalPlayerCards, playerAffectedByEnemyIdx),
        [updatedPlayerCards, finalPlayerCards, playerAffectedByEnemyIdx]
    );

    const playerSelfBuffStatusMap_PlayerSelf = useMemo(
        () => buildBuffStatusMap(primedPlayerCards, updatedPlayerCards, playerSelfAffectedByPlayerIdx),
        [primedPlayerCards, updatedPlayerCards, playerSelfAffectedByPlayerIdx]
    );

    const enemySelfBuffStatusMap_EnemySelf = useMemo(
        () => buildBuffStatusMap(updatedEnemyCards, finalEnemyCards, enemySelfAffectedByEnemyIdx),
        [updatedEnemyCards, finalEnemyCards, enemySelfAffectedByEnemyIdx]
    );

    /**
     * ✅ ACCURATE per-source highlight maps
     * Uses simulation-per-source so same-team buffs will always show.
     */
    const { oppMap: playerAffectsOppMap, selfMap: playerAffectsSelfMap } = useMemo(
        () => buildPerSourceDiffMaps(triggeredPlayerCards, primedPlayerCards, primedEnemyCards),
        [triggeredPlayerCards, primedPlayerCards, primedEnemyCards]
    );

    const { oppMap: enemyAffectsOppMap, selfMap: enemyAffectsSelfMap } = useMemo(
        () => buildPerSourceDiffMaps(triggeredEnemyCards, updatedEnemyCards, updatedPlayerCards),
        [triggeredEnemyCards, updatedEnemyCards, updatedPlayerCards]
    );

    /** =========================================================
     * PRE-ANIM "BOOSTS" FLASH
     * ========================================================= */
    const boostAffectedIdx = useMemo(() => {
        const base = new Set(guildIdx);
        factionIdx.forEach((i) => base.add(i));
        return Array.from(base.values());
    }, [guildIdx, factionIdx]);

    const boostGlowMap = useMemo(() => {
        const map = {};
        boostAffectedIdx.forEach((i) => {
            map[i] = GOLD;
        });
        return map;
    }, [boostAffectedIdx]);

    // State machine
    const [phase, setPhase] = useState('idle');
    const [activeSide, setActiveSide] = useState(null);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [showResultBanner, setShowResultBanner] = useState(false);

    // two highlight sets:
    // - affectedOppNow highlights opposing row during activation
    // - affectedSelfNow highlights acting side row (self/team buffs)
    const [affectedOppNow, setAffectedOppNow] = useState([]);
    const [affectedSelfNow, setAffectedSelfNow] = useState([]);

    const [showBoostBadges, setShowBoostBadges] = useState(false);
    const [showStartBanner, setShowStartBanner] = useState(false);

    // Guard: ensure animation pipeline runs once per `open` cycle
    const animationHasRunRef = useRef(false);

    // Fallback art cache only if parent didn't pass getImageForCard
    const [imageMap, setImageMap] = useState({});
    const preloadRunRef = useRef(false);

    const getImageForCardLocal = useCallback(
        (card) => {
            const key = getCardImageKey(card);
            if (!key) return undefined;
            return imageMap[key];
        },
        [imageMap]
    );

    // ✅ Decide which resolver we use everywhere in this dialog
    const getImageForCardFinal = getImageForCardProp || getImageForCardLocal;

    /**
     * FALLBACK IMAGE PRELOAD
     * Only runs if parent did NOT pass getImageForCard
     */
    useEffect(() => {
        if (!open) return;
        if (getImageForCardProp) return; // parent handles preload
        if (!globalCards) return;
        if (preloadRunRef.current) return;

        preloadRunRef.current = true;

        const allBattleCards = [...primedPlayerCards.slice(0, 5), ...primedEnemyCards.slice(0, 5)];

        (async () => {
            try {
                await preloadArtForCombatCards(allBattleCards, globalCards, setImageMap);
            } catch (e) {
                console.warn('[CombatDialog] bulk art preload failed:', e);
            }
        })();
    }, [open, getImageForCardProp, primedPlayerCards, primedEnemyCards, globalCards]);

    /**
     * ANIMATION EFFECT
     * Runs exactly once per `open` cycle.
     */
    useEffect(() => {
        if (!open) return;
        if (animationHasRunRef.current) return;
        animationHasRunRef.current = true;

        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

        (async () => {
            setShowResultBanner(false);
            setAffectedOppNow([]);
            setAffectedSelfNow([]);
            setActiveIndex(-1);
            setActiveSide(null);
            setPhase('idle');
            setShowBoostBadges(false);

            // Start banner
            setShowStartBanner(true);
            await sleep(START_BANNER_MS);
            setShowStartBanner(false);

            // Boosts flash
            if (anyPassiveBoosts && boostAffectedIdx.length > 0) {
                setPhase('boosts');
                setActiveSide('boosts-player');
                setAffectedOppNow(boostAffectedIdx);
                setAffectedSelfNow([]);
                setShowBoostBadges(true);
                await sleep(ACTIVE_POP_MS);

                setAffectedOppNow([]);
                setShowBoostBadges(false);
                await sleep(SETTLE_MS);
            }

            async function animateSide(cards, side, oppMap, selfMap) {
                const trigIdxs = (cards || [])
                    .slice(0, 5)
                    .map((c, idx) => (c?.triggered && hasAbility(c) ? idx : -1))
                    .filter((x) => x >= 0);

                for (let z = 0; z < trigIdxs.length; z++) {
                    const idx = trigIdxs[z];
                    setActiveSide(side);
                    setActiveIndex(idx);

                    setAffectedOppNow(oppMap?.[idx] || []);
                    setAffectedSelfNow(selfMap?.[idx] || []);

                    await sleep(ACTIVE_POP_MS);

                    setActiveIndex(-1);
                    setAffectedOppNow([]);
                    setAffectedSelfNow([]);
                    await sleep(SETTLE_MS);
                }
            }

            setPhase('attacker');
            await animateSide(
                triggeredPlayerCards,
                'player',
                playerAffectsOppMap,
                playerAffectsSelfMap
            );

            setPhase('defender');
            await animateSide(
                triggeredEnemyCards,
                'enemy',
                enemyAffectsOppMap,
                enemyAffectsSelfMap
            );

            setPhase('done');
            await sleep(200);
            setShowResultBanner(true);
        })();
    }, [
        open,
        anyPassiveBoosts,
        boostAffectedIdx,
        triggeredPlayerCards,
        triggeredEnemyCards,
        playerAffectsOppMap,
        playerAffectsSelfMap,
        enemyAffectsOppMap,
        enemyAffectsSelfMap,
    ]);

    // One-time resolve payload
    const didResolveRef = useRef(false);
    useEffect(() => {
        if (!open || didResolveRef.current) return;
        if (!basePlayerCards.length || !baseEnemyCards.length) return;
        if (!resolvedPlayerId) {
            console.warn('[CombatDialog] Missing playerId/userData.uid; not resolving.');
            return;
        }

        const enemyId = safeEnemy.enemyId || safeEnemy.uid || safeEnemy.userId || safeEnemy.id || null;

        const payload = {
            actorId: resolvedPlayerId,
            enemyId,
            playerWins,
            winnerPower: playerWins ? playerPower : enemyPower,
            loserPower: playerWins ? enemyPower : playerPower,
            context: mode,
            trophiesEnabled,
            trophyDelta: trophiesEnabled ? trophyDelta : 0,
        };

        if (enemyId) {
            payload.winnerId = playerWins ? resolvedPlayerId : enemyId;
            payload.loserId = playerWins ? enemyId : resolvedPlayerId;
        }

        didResolveRef.current = true;
        onResolved?.(payload);
    }, [
        open,
        basePlayerCards.length,
        baseEnemyCards.length,
        resolvedPlayerId,
        safeEnemy.enemyId,
        safeEnemy.uid,
        safeEnemy.userId,
        safeEnemy.id,
        playerWins,
        playerPower,
        enemyPower,
        onResolved,
        mode,
        trophiesEnabled,
        trophyDelta,
    ]);

    // Reset guards whenever dialog closes
    useEffect(() => {
        if (!open) {
            didResolveRef.current = false;
            animationHasRunRef.current = false;
            preloadRunRef.current = false;
            setPhase('idle');
            setActiveSide(null);
            setActiveIndex(-1);
            setAffectedOppNow([]);
            setAffectedSelfNow([]);
            setShowResultBanner(false);
            setShowStartBanner(false);
            setShowBoostBadges(false);
        }
    }, [open]);

    const handleCloseAll = () => {
        onClose && onClose();
    };

    const opponentName = safeEnemy.username || safeEnemy.name || 'Opponent';

    // Decide which indices affect which row
    const isBoostsPhase = phase === 'boosts' && activeSide === 'boosts-player';

    const playerRowAffected = isBoostsPhase
        ? affectedOppNow
        : activeSide === 'enemy'
        ? affectedOppNow
        : activeSide === 'player'
        ? affectedSelfNow
        : [];

    const enemyRowAffected = isBoostsPhase
        ? []
        : activeSide === 'player'
        ? affectedOppNow
        : activeSide === 'enemy'
        ? affectedSelfNow
        : [];

    // Glow maps per row
    const playerRowGlowMap = isBoostsPhase
        ? boostGlowMap
        : activeSide === 'enemy'
        ? playerGlowMap_EnemyToPlayer
        : activeSide === 'player'
        ? playerSelfGlowMap_PlayerSelf
        : {};

    const enemyRowGlowMap =
        activeSide === 'player'
            ? enemyGlowMap_PlayerToEnemy
            : activeSide === 'enemy'
            ? enemySelfGlowMap_EnemySelf
            : {};

    // Buff status maps per row
    // ✅ FIX: boosts phase passes EMPTY status map, so it can never show "Debuffed" during boosts
    const playerRowBuffStatusMap = isBoostsPhase
        ? {}
        : activeSide === 'enemy'
        ? playerBuffStatusMap_EnemyToPlayer
        : activeSide === 'player'
        ? playerSelfBuffStatusMap_PlayerSelf
        : {};

    const enemyRowBuffStatusMap = isBoostsPhase
        ? {}
        : activeSide === 'player'
        ? enemyBuffStatusMap_PlayerToEnemy
        : activeSide === 'enemy'
        ? enemySelfBuffStatusMap_EnemySelf
        : {};

    return (
        <Dialog
            open={open}
            onClose={() => {
                if (!showResultBanner) return;
                handleCloseAll();
            }}
            fullScreen
            PaperProps={{ sx: PaperBox }}
        >
            <DialogTitle sx={{ ...TitleText, flexShrink: 0 }}>
                Engage in Combat
                <Typography variant="caption" sx={{ ml: 1, color: alpha(TEXT_WARM, 0.7) }}>
                    {mode === 'arena'
                        ? '(Arena Battle)'
                        : mode === 'campaign'
                        ? '(Campaign Encounter)'
                        : '(Boss Battle)'}
                </Typography>
            </DialogTitle>

            <DialogContent
                sx={{
                    flexGrow: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    pb: 2,
                }}
            >
                {showStartBanner && (
                    <>
                        <Box sx={{ position: 'absolute', inset: 0, zIndex: 10 }} />
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 11,
                                pointerEvents: 'none',
                                px: 2,
                            }}
                        >
                            <motion.div
                                initial={{ scale: 0.7, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: 'spring', stiffness: 220, damping: 24 }}
                                style={{
                                    padding: '18px 28px',
                                    borderRadius: 16,
                                    border: `1px solid ${alpha(GOLD, 0.7)}`,
                                    background: `linear-gradient(180deg, ${alpha('#000', 0.75)} 0%, ${alpha(
                                        '#0b0b0b',
                                        0.95
                                    )} 100%)`,
                                    boxShadow: `0 0 0 1px ${alpha(GOLD, 0.25)} inset, 0 24px 64px ${alpha(
                                        GOLD,
                                        0.25
                                    )}`,
                                    minWidth: 380,
                                }}
                            >
                                <Typography
                                    variant="h3"
                                    sx={{
                                        color: TEXT_WARM,
                                        fontFamily: `'Cinzel', serif`,
                                        letterSpacing: '1.2px',
                                        textAlign: 'center',
                                        textShadow: `0 0 20px ${alpha(GOLD, 0.4)}`,
                                        lineHeight: 1.1,
                                    }}
                                >
                                    BATTLE START
                                </Typography>
                            </motion.div>
                        </Box>
                    </>
                )}

                <Box sx={{ width: '90%', maxWidth: 1200 }}>
                    <Box sx={{ mb: 5 }}>
                        <DeckRow
                            cards={finalPlayerCards}
                            side="player"
                            title="Your Cards"
                            affectedIndices={playerRowAffected}
                            affectGlowMap={playerRowGlowMap}
                            boostBadgesMap={showBoostBadges ? passiveBoostLabelMap : {}}
                            buffStatusMap={playerRowBuffStatusMap}
                            activeSide={activeSide}
                            activeIndex={activeIndex}
                            showBoostBadges={showBoostBadges}
                            getImageForCard={getImageForCardFinal}
                        />
                    </Box>

                    <DeckRow
                        cards={finalEnemyCards}
                        side="enemy"
                        title={`${opponentName}'s Cards`}
                        affectedIndices={enemyRowAffected}
                        affectGlowMap={enemyRowGlowMap}
                        boostBadgesMap={{}}
                        buffStatusMap={enemyRowBuffStatusMap}
                        activeSide={activeSide}
                        activeIndex={activeIndex}
                        showBoostBadges={showBoostBadges}
                        getImageForCard={getImageForCardFinal}
                    />
                </Box>

                {showResultBanner && (
                    <>
                        <Box sx={{ position: 'absolute', inset: 0, zIndex: 8 }} />
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 9,
                                pointerEvents: 'none',
                                px: 2,
                            }}
                        >
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: 'spring', stiffness: 200, damping: 22 }}
                                style={{
                                    padding: '22px 32px',
                                    borderRadius: 16,
                                    border: `1px solid ${alpha(GOLD, 0.7)}`,
                                    background: `linear-gradient(180deg, ${alpha('#000', 0.75)} 0%, ${alpha(
                                        '#0b0b0b',
                                        0.95
                                    )} 100%)`,
                                    boxShadow: `0 0 0 1px ${alpha(GOLD, 0.2)} inset, 0 24px 64px ${alpha(
                                        GOLD,
                                        0.22
                                    )}`,
                                    minWidth: 460,
                                }}
                            >
                                <Typography
                                    variant="h2"
                                    sx={{
                                        color: TEXT_WARM,
                                        fontFamily: `'Cinzel', serif`,
                                        letterSpacing: '1.2px',
                                        textAlign: 'center',
                                        textShadow: `0 0 20px ${alpha(GOLD, 0.35)}`,
                                        lineHeight: 1.1,
                                    }}
                                >
                                    {resultText}
                                </Typography>

                                {!playerWins && (
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mt: 1.25,
                                            textAlign: 'center',
                                            color: alpha(TEXT_WARM, 0.92),
                                            fontFamily: `'Cinzel', serif`,
                                            lineHeight: 1.35,
                                            maxWidth: 520,
                                            mx: 'auto',
                                            textShadow: `0 0 14px ${alpha(GOLD, 0.18)}`,
                                        }}
                                    >
                                        Tip: Level up your cards in your Profile, or open card packs from the Shop to boost your power.
                                    </Typography>
                                )}

                                <Box sx={{ mt: 1.25, display: 'flex', gap: 2, justifyContent: 'center' }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}
                                    >
                                        ATK: {fmt(playerAttack)}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}
                                    >
                                        DEF: {fmt(enemyDefense)}
                                    </Typography>
                                </Box>

                                {trophiesEnabled && (
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            mt: 0.75,
                                            textAlign: 'center',
                                            color: TEXT_WARM,
                                            fontFamily: `'Cinzel', serif`,
                                        }}
                                    >
                                        {playerWins ? `+${fmt(trophyDelta)}` : `−${fmt(trophyDelta)}`}{' '}
                                        Trophies
                                    </Typography>
                                )}

                                {mode === 'campaign' && playerWins && hasCampaignReward && (
                                    <Box
                                        sx={{
                                            mt: 1.25,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        <Typography
                                            variant="h5"
                                            sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}
                                        >
                                            Rewards
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            {rewardLines.map((line, idx) => (
                                                <Box
                                                    key={`rw-${idx}`}
                                                    sx={{
                                                        px: 1.25,
                                                        py: 0.5,
                                                        borderRadius: 1.25,
                                                        border: `1px solid ${alpha(GOLD, 0.55)}`,
                                                        background: `linear-gradient(180deg, ${alpha(
                                                            GOLD,
                                                            0.18
                                                        )} 0%, ${alpha(GOLD, 0.08)} 100%)`,
                                                        boxShadow: `0 0 0 1px ${alpha(GOLD, 0.18)} inset`,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}
                                                    >
                                                        {line}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </motion.div>
                        </Box>
                    </>
                )}
            </DialogContent>

            {showResultBanner && (
                <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button
                        onClick={handleCloseAll}
                        sx={{
                            fontWeight: 700,
                            letterSpacing: '.6px',
                            color: TEXT_WARM,
                            borderRadius: 2,
                            px: 2,
                            py: 1,
                            border: `1px solid ${alpha(GOLD, 0.55)}`,
                            background: `linear-gradient(180deg, ${alpha(GOLD, 0.25)} 0%, ${alpha(
                                GOLD,
                                0.15
                            )} 100%)`,
                            boxShadow: `0 8px 24px ${alpha(GOLD, 0.18)}`,
                            textTransform: 'none',
                            '&:hover': {
                                background: `linear-gradient(180deg, ${alpha(GOLD, 0.35)} 0%, ${alpha(
                                    GOLD,
                                    0.2
                                )} 100%)`,
                                boxShadow: `0 10px 28px ${alpha(GOLD, 0.28)}`,
                            },
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}

export default CombatDialog;
